/* Allianz Insurance Portal — Wave III pages (iter-92 · Feb 2026).
   5 new exports mounted under `/insurance/*` alongside the existing 4 pages:
     • InsuranceWallet         — Allianz clearing wallet + topup/withdraw + 30d KPIs
     • InsuranceLoansRoster    — every credit-line visible to Allianz (lender + seller funded)
     • InsuranceTakeoverQueue  — loans at day-55+ awaiting approve/reject decision
     • InsuranceCollections    — post-takeover buyer payment tracking
     • InsuranceAnalytics      — exposure, pipeline, projected payouts, top buyers

   Theme: emerald (matches InsurancePages.jsx).
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Wallet, ArrowDownToLine, ArrowUpFromLine, RefreshCw, FileText,
  AlertTriangle, ShieldCheck, BarChart3, Send, X, CheckCircle, Banknote,
  TrendingUp, ScrollText, Activity,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const fmtMoney = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);
const fmtMoneyP = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(n);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };
const fmtDT = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

const STATUS_TONE = {
  active: "emerald", past_due: "amber",
  past_due_tier2: "orange", past_due_tier3: "rose",
  insurance_takeover: "violet", paid: "slate",
  nsf_pending: "rose", extended: "sky", void: "slate",
  open: "amber", closed: "emerald",
};

function Kpi({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || "slate";
  return (
    <Badge data-testid={`status-${status}`} variant="outline"
      className={`bg-${tone}-50 border-${tone}-200 text-${tone}-700 capitalize`}>
      {(status || "—").replace(/_/g, " ")}
    </Badge>
  );
}

// ════════════════════════════════════════════════════════════════════════
// WALLET
// ════════════════════════════════════════════════════════════════════════
export function InsuranceWallet() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/partner/insurance/wallet");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wallet");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="ins-wallet-page" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Wallet className="w-6 h-6 text-emerald-500"/> Allianz Clearing Wallet
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pre-funded clearing account that pays funders when buyers default at day-60.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="ins-wallet-topup-btn" onClick={() => setTopupOpen(true)}>
            <ArrowDownToLine className="w-4 h-4 mr-1.5"/> Top up
          </Button>
          <Button data-testid="ins-wallet-withdraw-btn" variant="outline" onClick={() => setWithdrawOpen(true)}>
            <ArrowUpFromLine className="w-4 h-4 mr-1.5"/> Withdraw
          </Button>
          <Button data-testid="ins-wallet-refresh" variant="outline" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
          </Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Kpi testid="ins-kpi-balance" label="Balance"
              value={fmtMoneyP(data.balance)} icon={Banknote} tone="emerald"/>
            <Kpi testid="ins-kpi-currency" label="Currency"
              value={data.currency || "USD"} icon={Wallet} tone="slate"/>
            <Kpi testid="ins-kpi-inflow-30d" label="Inflows · 30d"
              value={fmtMoney(data.kpis_30d?.inflow_usd)}
              sub={`${data.kpis_30d?.inflow_count || 0} txns`}
              icon={ArrowDownToLine} tone="sky"/>
            <Kpi testid="ins-kpi-outflow-30d" label="Outflows · 30d"
              value={fmtMoney(data.kpis_30d?.outflow_usd)}
              sub={`${data.kpis_30d?.outflow_count || 0} txns`}
              icon={ArrowUpFromLine} tone="rose"/>
            <Kpi testid="ins-kpi-wallet-id" label="Wallet ID"
              value={(data.wallet_user_id || "").slice(0, 16) + "…"}
              icon={ShieldCheck} tone="emerald"/>
          </div>

          <div className="rounded-xl border bg-white">
            <div className="px-4 py-3 border-b text-sm font-medium text-slate-700">
              Recent transactions
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Amount</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Method</th>
                    <th className="px-4 py-2.5">Reference</th>
                  </tr>
                </thead>
                <tbody data-testid="ins-wallet-txns">
                  {(data.transactions || []).map((t, i) => (
                    <tr key={t.id || i} data-testid={`ins-txn-${i}`} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-600 tabular-nums">{fmtDT(t.created_at)}</td>
                      <td className="px-4 py-2 text-slate-700">{t.type}</td>
                      <td className={`px-4 py-2 tabular-nums font-medium ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {t.amount >= 0 ? "+" : ""}{fmtMoneyP(t.amount)}
                      </td>
                      <td className="px-4 py-2"><StatusPill status={t.status}/></td>
                      <td className="px-4 py-2 text-slate-600">{t.method || "—"}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs font-mono">{t.ref || "—"}</td>
                    </tr>
                  ))}
                  {!data.transactions?.length && (
                    <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500 text-sm">
                      No transactions yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <TopupDialog open={topupOpen} onClose={() => setTopupOpen(false)} onDone={load} busy={busy} setBusy={setBusy}/>
      <WithdrawDialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} onDone={load} busy={busy} setBusy={setBusy}/>
    </div>
  );
}

function TopupDialog({ open, onClose, onDone, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("wire");
  const [reference, setReference] = useState("");
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/partner/insurance/wallet/topup", { amount: amt, method, reference: reference || undefined });
      toast.success(`Topped up ${fmtMoney(amt)}`);
      setAmount(""); setReference("");
      onClose(); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Topup failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="ins-topup-dialog">
        <DialogHeader>
          <DialogTitle>Top up Allianz wallet</DialogTitle>
          <DialogDescription>Pre-fund the clearing wallet to cover upcoming takeover payouts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Amount (USD)</Label>
            <Input data-testid="ins-topup-amount" type="number" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="100000"/>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="ins-topup-method"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="wire">Wire</SelectItem>
                <SelectItem value="ach">ACH</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference (optional)</Label>
            <Input data-testid="ins-topup-ref" value={reference}
              onChange={e => setReference(e.target.value)} placeholder="ALZ-2026-Q1-PREFUND"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="ins-topup-submit" onClick={submit} disabled={busy}>
            <Send className="w-4 h-4 mr-1.5"/>{busy ? "Sending…" : "Confirm top up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawDialog({ open, onClose, onDone, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("wire");
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/partner/insurance/wallet/withdraw", { amount: amt, method });
      toast.success(`Withdrew ${fmtMoney(amt)}`);
      setAmount("");
      onClose(); onDone();
    } catch (e) {
      const err = e?.response?.data;
      toast.error(err?.detail?.reason === "insufficient_funds"
        ? `Insufficient funds (balance: ${fmtMoneyP(err.detail.balance)})`
        : err?.detail || "Withdraw failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="ins-withdraw-dialog">
        <DialogHeader>
          <DialogTitle>Withdraw from Allianz wallet</DialogTitle>
          <DialogDescription>Pull funds back to your external bank.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Amount (USD)</Label>
            <Input data-testid="ins-withdraw-amount" type="number" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="50000"/>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="ins-withdraw-method"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="wire">Wire (same day)</SelectItem>
                <SelectItem value="ach">ACH (1-2 days)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="ins-withdraw-submit" onClick={submit} disabled={busy}>
            <Send className="w-4 h-4 mr-1.5"/>{busy ? "Sending…" : "Confirm withdraw"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
// LOANS ROSTER
// ════════════════════════════════════════════════════════════════════════
export function InsuranceLoansRoster() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [funderType, setFunderType] = useState("all");
  const [days, setDays] = useState(365);

  const load = async () => {
    try {
      const params = { since_days: days, limit: 200 };
      if (status !== "all") params.status = status;
      if (funderType !== "all") params.funder_type = funderType;
      const { data } = await api.get("/partner/insurance/loans-roster", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load loans roster");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, funderType, days]);

  return (
    <div data-testid="ins-loans-roster-page" className="space-y-5">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <ScrollText className="w-6 h-6 text-emerald-500"/> Loans Roster
          </h1>
          <p className="text-sm text-slate-600 mt-1">Every credit-line visible to Allianz across all funders.</p>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap items-end" data-testid="ins-roster-filters">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="ins-roster-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="past_due">Past due</SelectItem>
              <SelectItem value="past_due_tier2">Tier 2</SelectItem>
              <SelectItem value="past_due_tier3">Tier 3</SelectItem>
              <SelectItem value="insurance_takeover">Taken over</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Funder</Label>
          <Select value={funderType} onValueChange={setFunderType}>
            <SelectTrigger className="w-32 h-9" data-testid="ins-roster-funder"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="lender">Lender</SelectItem>
              <SelectItem value="seller">Seller</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Window</Label>
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="ins-roster-window"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
              <SelectItem value="1095">3 years</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} className="h-9" data-testid="ins-roster-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ins-kpi-exposure" label="Total exposure"
              value={fmtMoney(data.kpis?.total_exposure_usd)} icon={Banknote} tone="emerald"/>
            <Kpi testid="ins-kpi-outstanding" label="Outstanding loans"
              value={data.kpis?.outstanding_count ?? 0} icon={Activity} tone="amber"/>
            <Kpi testid="ins-kpi-takenover" label="Taken over"
              value={data.kpis?.takenover_count ?? 0} icon={ShieldCheck} tone="violet"/>
            <Kpi testid="ins-kpi-total" label="Total rows"
              value={data.total ?? 0} icon={ScrollText} tone="slate"/>
          </div>

          <div className="rounded-xl border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">Loan #</th>
                  <th className="px-4 py-2.5">Buyer</th>
                  <th className="px-4 py-2.5">Seller</th>
                  <th className="px-4 py-2.5">Funder</th>
                  <th className="px-4 py-2.5">Principal</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Due</th>
                  <th className="px-4 py-2.5">DPD</th>
                </tr>
              </thead>
              <tbody data-testid="ins-roster-table">
                {(data.rows || []).map((r, i) => (
                  <tr key={r.id} data-testid={`ins-roster-row-${i}`} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.loan_number}</td>
                    <td className="px-4 py-2 text-slate-700">{r.buyer_company || r.buyer_email || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{r.seller_company || r.seller_email || "—"}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`capitalize bg-${r.funder_type === "seller" ? "sky" : "violet"}-50 border-${r.funder_type === "seller" ? "sky" : "violet"}-200 text-${r.funder_type === "seller" ? "sky" : "violet"}-700`}>
                        {r.funder_type || "lender"}{r.lender_org_name ? ` · ${r.lender_org_name}` : ""}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums">{fmtMoneyP(r.principal_remaining ?? r.principal)}</td>
                    <td className="px-4 py-2"><StatusPill status={r.status}/></td>
                    <td className="px-4 py-2 text-slate-500 tabular-nums">{fmtDate(r.due_date)}</td>
                    <td className={`px-4 py-2 tabular-nums font-medium ${r.days_past_due > 0 ? "text-rose-700" : "text-slate-500"}`}>
                      {r.days_past_due > 0 ? `+${r.days_past_due}d` : "—"}
                    </td>
                  </tr>
                ))}
                {!data.rows?.length && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500 text-sm">No loans match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAKEOVER QUEUE
// ════════════════════════════════════════════════════════════════════════
export function InsuranceTakeoverQueue() {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(55);
  const [decision, setDecision] = useState(null); // {loan, action: 'approve'|'reject'}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/partner/insurance/takeover-queue", {
        params: { threshold_days: threshold },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load takeover queue");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const submitDecision = async () => {
    if ((reason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusy(true);
    try {
      const path = `/partner/insurance/takeover/${decision.loan.id}/${decision.action}`;
      await api.post(path, { reason });
      toast.success(decision.action === "approve" ? "Takeover approved · payout sent" : "Takeover refused");
      setDecision(null); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ins-takeover-queue-page" className="space-y-5">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <AlertTriangle className="w-6 h-6 text-amber-500"/> Takeover Queue
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Loans nearing day-60 — review and approve before auto-takeover fires.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Threshold</Label>
            <Select value={String(threshold)} onValueChange={v => setThreshold(Number(v))}>
              <SelectTrigger className="w-32 h-9" data-testid="ins-queue-threshold"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="45">45 days</SelectItem>
                <SelectItem value="55">55 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="75">75 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="ins-queue-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
          </Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Kpi testid="ins-queue-kpi-total" label="Loans in queue"
              value={data.total ?? 0} icon={AlertTriangle} tone="amber"/>
            <Kpi testid="ins-queue-kpi-threshold" label="Threshold"
              value={`≥ ${data.threshold_days}d past due`} icon={Activity} tone="slate"/>
            <Kpi testid="ins-queue-kpi-window" label="Review window"
              value={`${Math.max(0, 60 - data.threshold_days)} days`}
              sub="before auto-takeover fires"
              icon={ShieldCheck} tone="emerald"/>
          </div>

          <div className="rounded-xl border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">Loan #</th>
                  <th className="px-4 py-2.5">Buyer</th>
                  <th className="px-4 py-2.5">Seller</th>
                  <th className="px-4 py-2.5">Funder</th>
                  <th className="px-4 py-2.5">Total owed</th>
                  <th className="px-4 py-2.5">DPD</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Decision</th>
                </tr>
              </thead>
              <tbody data-testid="ins-queue-table">
                {(data.rows || []).map((r, i) => (
                  <tr key={r.id} data-testid={`ins-queue-row-${i}`} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.loan_number}</td>
                    <td className="px-4 py-2 text-slate-700">{r.buyer_company || r.buyer_email || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{r.seller_email || "—"}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`capitalize bg-${r.funder_type === "seller" ? "sky" : "violet"}-50 border-${r.funder_type === "seller" ? "sky" : "violet"}-200 text-${r.funder_type === "seller" ? "sky" : "violet"}-700`}>
                        {r.funder_type || "lender"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums font-medium">{fmtMoneyP(r.total_owed)}</td>
                    <td className="px-4 py-2 tabular-nums text-rose-700 font-medium">+{r.days_past_due}d</td>
                    <td className="px-4 py-2"><StatusPill status={r.status}/></td>
                    <td className="px-4 py-2 flex gap-1.5">
                      <Button size="sm" data-testid={`ins-queue-approve-${i}`}
                        onClick={() => { setDecision({ loan: r, action: "approve" }); setReason(""); }}
                        className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="w-3.5 h-3.5 mr-1"/> Approve
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`ins-queue-reject-${i}`}
                        onClick={() => { setDecision({ loan: r, action: "reject" }); setReason(""); }}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50">
                        <X className="w-3.5 h-3.5 mr-1"/> Refuse
                      </Button>
                    </td>
                  </tr>
                ))}
                {!data.rows?.length && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500 text-sm">Queue is clear.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
        <DialogContent data-testid="ins-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.action === "approve" ? "Approve takeover" : "Refuse takeover"}
            </DialogTitle>
            <DialogDescription>
              {decision?.action === "approve"
                ? `Pay ${fmtMoneyP(decision?.loan?.total_owed)} to the funder and assume the debt.`
                : "Refuse coverage. Loan stays with the funder."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason (audit-required, ≥3 chars)</Label>
            <Textarea data-testid="ins-decision-reason" value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={decision?.action === "approve"
                ? "Buyer default confirmed by Allianz review"
                : "Cover does not extend to this exposure tier"}/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="ins-decision-submit" onClick={submitDecision} disabled={busy}
              className={decision?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              variant={decision?.action === "approve" ? "default" : "destructive"}>
              {busy ? "Submitting…" : (decision?.action === "approve" ? "Approve & pay funder" : "Refuse coverage")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// COLLECTIONS
// ════════════════════════════════════════════════════════════════════════
export function InsuranceCollections() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [days, setDays] = useState(365);
  const [paymentFor, setPaymentFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("wire");
  const [payRef, setPayRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = { since_days: days, limit: 200 };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/partner/insurance/collections", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load collections");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, days]);

  const submitPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/partner/insurance/collections/${paymentFor.id}/log-payment`, {
        amount: amt, method: payMethod, reference: payRef || undefined,
      });
      toast.success(`Payment ${fmtMoney(amt)} recorded`);
      setPaymentFor(null); setPayAmount(""); setPayRef("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Log-payment failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ins-collections-page" className="space-y-5">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <FileText className="w-6 h-6 text-emerald-500"/> Collections
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Post-takeover debt — Allianz collects directly from defaulted buyers.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-32 h-9" data-testid="ins-coll-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Window</Label>
            <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
              <SelectTrigger className="w-32 h-9" data-testid="ins-coll-window"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="1095">3 years</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="ins-coll-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
          </Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ins-coll-kpi-total" label="Collections"
              value={data.kpis?.total_collections ?? 0} icon={FileText} tone="slate"/>
            <Kpi testid="ins-coll-kpi-balance" label="Outstanding balance"
              value={fmtMoney(data.kpis?.outstanding_balance_usd)} icon={AlertTriangle} tone="rose"/>
            <Kpi testid="ins-coll-kpi-collected" label="Collected so far"
              value={fmtMoney(data.kpis?.collected_so_far_usd)} icon={CheckCircle} tone="emerald"/>
            <Kpi testid="ins-coll-kpi-original" label="Original payouts"
              value={fmtMoney(data.kpis?.original_payouts_usd)} icon={Banknote} tone="violet"/>
          </div>

          <div className="rounded-xl border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">Loan #</th>
                  <th className="px-4 py-2.5">Buyer</th>
                  <th className="px-4 py-2.5">Original</th>
                  <th className="px-4 py-2.5">Collected</th>
                  <th className="px-4 py-2.5">Balance</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Opened</th>
                  <th className="px-4 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody data-testid="ins-coll-table">
                {(data.rows || []).map((r, i) => (
                  <tr key={r.id} data-testid={`ins-coll-row-${i}`} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.loan_number}</td>
                    <td className="px-4 py-2 text-slate-700">{r.buyer_company || r.buyer_email || "—"}</td>
                    <td className="px-4 py-2 tabular-nums">{fmtMoneyP(r.original_payout)}</td>
                    <td className="px-4 py-2 tabular-nums text-emerald-700">{fmtMoneyP(r.collected_usd)}</td>
                    <td className="px-4 py-2 tabular-nums font-medium text-rose-700">{fmtMoneyP(r.balance_owed_usd)}</td>
                    <td className="px-4 py-2"><StatusPill status={r.status}/></td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{fmtDate(r.opened_at)}</td>
                    <td className="px-4 py-2">
                      {r.status === "open" ? (
                        <Button size="sm" data-testid={`ins-coll-log-${i}`}
                          onClick={() => { setPaymentFor(r); setPayAmount(""); setPayRef(""); }}>
                          <ArrowDownToLine className="w-3.5 h-3.5 mr-1"/> Log payment
                        </Button>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!data.rows?.length && (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500 text-sm">No collections yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!paymentFor} onOpenChange={() => setPaymentFor(null)}>
        <DialogContent data-testid="ins-coll-pay-dialog">
          <DialogHeader>
            <DialogTitle>Log buyer payment · {paymentFor?.loan_number}</DialogTitle>
            <DialogDescription>
              Balance remaining: {fmtMoneyP(paymentFor?.balance_owed_usd)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Amount (USD)</Label>
              <Input data-testid="ins-coll-pay-amount" type="number" value={payAmount}
                onChange={e => setPayAmount(e.target.value)} placeholder="1000"/>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="ins-coll-pay-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wire">Wire</SelectItem>
                  <SelectItem value="ach">ACH</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference (optional)</Label>
              <Input data-testid="ins-coll-pay-ref" value={payRef}
                onChange={e => setPayRef(e.target.value)} placeholder="WIRE-2026-0314-A"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentFor(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="ins-coll-pay-submit" onClick={submitPayment} disabled={busy}>
              {busy ? "Logging…" : "Log payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ANALYTICS
// ════════════════════════════════════════════════════════════════════════
export function InsuranceAnalytics() {
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/partner/insurance/analytics");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="ins-analytics-page" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <BarChart3 className="w-6 h-6 text-emerald-500"/> Analytics
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Aggregate exposure, claim pipeline, projected payouts.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="ins-analytics-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5"/> Refresh
        </Button>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ins-an-kpi-exposure" label="Total exposure"
              value={fmtMoney(data.exposure?.total_principal_usd)}
              sub={`${data.exposure?.open_loans || 0} open loans`}
              icon={Banknote} tone="emerald"/>
            <Kpi testid="ins-an-kpi-proj" label="Projected 30d payout"
              value={fmtMoney(data.projected_30d_payouts_usd)}
              icon={TrendingUp} tone="amber"/>
            <Kpi testid="ins-an-kpi-lender-active" label="Lenders covered"
              value={data.lender_coverage?.active_count ?? 0}
              sub="active policies"
              icon={ShieldCheck} tone="violet"/>
            <Kpi testid="ins-an-kpi-lender-limit" label="Aggregate lender limit"
              value={fmtMoney(data.lender_coverage?.total_limit_usd)}
              icon={Wallet} tone="sky"/>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="text-sm font-medium text-slate-700 mb-3">Claim pipeline (by status)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(data.pipeline || {}).map(([k, v]) => (
                <Kpi key={k} testid={`ins-an-pipe-${k}`}
                  label={k.replace(/_/g, " ")} value={v.count}
                  sub={fmtMoney(v.principal_usd)}
                  tone={k === "insurance_takeover" ? "violet" : k.includes("tier3") ? "rose" : k.includes("tier2") ? "orange" : "amber"}/>
              ))}
              {Object.keys(data.pipeline || {}).length === 0 && (
                <div className="col-span-5 text-center text-slate-500 text-sm py-4">No loans in pipeline.</div>
              )}
            </div>
          </div>

          <div className="rounded-xl border bg-white">
            <div className="px-4 py-3 border-b text-sm font-medium text-slate-700">
              Top buyers by exposure
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2.5">Rank</th>
                    <th className="px-4 py-2.5">Buyer</th>
                    <th className="px-4 py-2.5">Loans</th>
                    <th className="px-4 py-2.5">Total exposure</th>
                  </tr>
                </thead>
                <tbody data-testid="ins-an-top-buyers">
                  {(data.top_buyers || []).map((b, i) => (
                    <tr key={b._id || i} data-testid={`ins-an-top-${i}`} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-500 font-medium">#{i + 1}</td>
                      <td className="px-4 py-2 text-slate-700">{b.buyer_company || b.buyer_email || b._id || "—"}</td>
                      <td className="px-4 py-2 tabular-nums">{b.loans}</td>
                      <td className="px-4 py-2 tabular-nums font-medium">{fmtMoneyP(b.principal_usd)}</td>
                    </tr>
                  ))}
                  {!data.top_buyers?.length && (
                    <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500 text-sm">No exposure data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// INSURANCE FORECAST (iter-98) — Allianz wallet runway + projected payouts
// ════════════════════════════════════════════════════════════════════════
export function InsuranceForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(14);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/partner/insurance/forecast?runway_alert_days=${threshold}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load forecast"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const proj = data?.projections || {};
  const alertTone = data?.runway_alert ? "rose" : "emerald";

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ins-partner-forecast">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" /> Forecast
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Allianz clearing-wallet runway + projected takeover payouts.
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="forecast-refresh" disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Wallet balance" value={fmtMoneyP(data?.wallet_balance_usd ?? 0)} icon={Wallet} tone="emerald" testid="forecast-kpi-balance"/>
        <Kpi label="Avg daily burn · 90d" value={fmtMoneyP(data?.avg_daily_burn_usd ?? 0)} icon={ArrowUpFromLine} tone="slate"/>
        <Kpi label="Runway · days"
              value={data?.runway_days == null ? "∞" : `${data?.runway_days}`}
              icon={Activity}
              tone={alertTone} testid="forecast-kpi-runway"/>
        <Kpi label="Open insured loans" value={data?.open_loan_count ?? 0} icon={FileText} tone="sky"/>
        <Kpi label="Alert" value={data?.runway_alert ? "On" : "Off"} icon={AlertTriangle} tone={alertTone}/>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Alert threshold</Label>
        <Select value={String(threshold)} onValueChange={(v) => setThreshold(parseInt(v))}>
          <SelectTrigger className="w-32 h-9" data-testid="forecast-threshold">
            <SelectValue placeholder="14d"/>
          </SelectTrigger>
          <SelectContent>
            {[7, 14, 21, 30, 45, 60].map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" className={`bg-${alertTone}-50 text-${alertTone}-700 border-${alertTone}-200`}>
          {data?.runway_alert ? "Runway low — pre-fund wallet" : "Runway healthy"}
        </Badge>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Projected payouts</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[30, 60, 90, 120].map(w => (
            <div key={w} className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3" data-testid={`forecast-window-${w}`}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Next {w} days</div>
              <div className="text-xl font-semibold tabular-nums mt-1">{fmtMoneyP(proj[w]?.total_usd ?? 0)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{proj[w]?.loan_count ?? 0} loans · principal {fmtMoneyP(proj[w]?.principal_usd ?? 0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Daily timeline · next 120 days</h3>
          <span className="text-xs text-slate-500">{(data?.daily_timeline || []).length} bucket(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Takeover date</th>
                <th className="px-5 py-2.5 font-medium">Loans</th>
                <th className="px-5 py-2.5 font-medium">Projected amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.daily_timeline || []).slice(0, 60).map((x, i) => (
                <tr key={i} data-testid={`forecast-row-${i}`}>
                  <td className="px-5 py-2 font-mono text-xs">{x.date}</td>
                  <td className="px-5 py-2 tabular-nums">{x.loan_count}</td>
                  <td className="px-5 py-2 tabular-nums font-medium text-rose-700">{fmtMoneyP(x.amount_usd)}</td>
                </tr>
              ))}
              {!loading && !(data?.daily_timeline || []).length && (
                <tr><td colSpan="3" className="px-5 py-10 text-center text-slate-500 text-sm">No projected takeovers in the next 120 days.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// WAVE III (iter-99) — UNIFIED POLICIES ROSTER
// ════════════════════════════════════════════════════════════════════════
const POLICY_TYPE_LABEL = {
  seller_book: "Seller Book",
  lender_book: "Lender Book",
  buyer_underwriting: "Buyer Underwriting",
};

export function InsurancePolicies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [policyType, setPolicyType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(365);
  const [decideOpen, setDecideOpen] = useState(null); // policy
  const [decision, setDecision] = useState("approved");
  const [reason, setReason] = useState("");
  const [limitOverride, setLimitOverride] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ since_days: String(sinceDays), limit: "200" });
      if (policyType) params.set("policy_type", policyType);
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      const r = await api.get(`/partner/insurance/policies?${params.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load policies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [policyType, status, sinceDays]);

  const submitDecision = async () => {
    if ((reason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/partner/insurance/policies/${decideOpen.id}/decide`, {
        decision, reason,
        limit_override_usd: limitOverride ? parseFloat(limitOverride) : undefined,
      });
      toast.success(`Policy ${decision} · ${decideOpen.policy_number}`);
      setDecideOpen(null); setReason(""); setLimitOverride("");
      load();
    } catch (e) {
      const err = e.response?.data?.detail || e.response?.data;
      toast.error(typeof err === "string" ? err : (err?.reason || "decide failed"));
    } finally { setBusy(false); }
  };

  const k = data?.kpis || {};
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ins-policies">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-600" /> Policies
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Approve seller books · buyer underwriting · lender books. Same engine, three policy types.
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="policies-refresh" disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Policies" value={k.total_count ?? 0} icon={ShieldCheck} tone="emerald"/>
        <Kpi label="Pending review" value={k.pending_count ?? 0} icon={AlertTriangle} tone="amber" testid="policies-kpi-pending"/>
        <Kpi label="Active" value={k.active_count ?? 0} icon={CheckCircle} tone="emerald"/>
        <Kpi label="Total limit" value={fmtMoney(k.total_limit_usd ?? 0)} icon={Banknote} tone="sky"/>
        <Kpi label="Total used" value={fmtMoney(k.total_used_usd ?? 0)} icon={Activity} tone="slate"/>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={policyType || "all"} onValueChange={(v) => setPolicyType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 h-9" data-testid="policies-type"><SelectValue placeholder="Type"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="seller_book">Seller book</SelectItem>
            <SelectItem value="buyer_underwriting">Buyer underwriting</SelectItem>
            <SelectItem value="lender_book">Lender book</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-9" data-testid="policies-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(parseInt(v))}>
          <SelectTrigger className="w-32 h-9"><SelectValue/></SelectTrigger>
          <SelectContent>
            {[30, 90, 180, 365, 1095].map(d => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search number / holder" className="h-9 w-64" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} data-testid="policies-search"/>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Policy #</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Holder</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Limit</th>
                <th className="px-4 py-2.5 font-medium">Used</th>
                <th className="px-4 py-2.5 font-medium">Available</th>
                <th className="px-4 py-2.5 font-medium">Requested</th>
                <th className="px-4 py-2.5 font-medium">Created</th>
                <th className="px-4 py-2.5 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.rows || []).map((p, i) => (
                <tr key={p.id} data-testid={`policy-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-[10.5px]">{p.policy_number}</td>
                  <td className="px-4 py-2 text-[11px]"><Badge variant="outline">{POLICY_TYPE_LABEL[p.policy_type] || p.policy_type}</Badge></td>
                  <td className="px-4 py-2">
                    <div className="text-[12.5px] font-medium">{p.policy_holder_name || "—"}</div>
                    <div className="text-[10.5px] text-slate-500 font-mono">{(p.policy_holder_id || "").slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-2"><StatusPill status={p.status}/></td>
                  <td className="px-4 py-2 tabular-nums">{fmtMoney(p.total_limit_usd)}</td>
                  <td className="px-4 py-2 tabular-nums">{fmtMoney(p.used_usd)}</td>
                  <td className="px-4 py-2 tabular-nums text-emerald-700 font-medium">{fmtMoney(p.available_usd)}</td>
                  <td className="px-4 py-2 tabular-nums text-slate-500">{fmtMoney(p.requested_limit_usd)}</td>
                  <td className="px-4 py-2 text-[11px] text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-2 text-right">
                    {p.status === "pending" && (
                      <Button size="sm" data-testid={`policy-decide-${p.id?.slice(0,6)}`} className="h-7 rounded-full text-[11px]"
                        onClick={() => { setDecideOpen(p); setDecision("approved"); setLimitOverride(String(p.requested_limit_usd || "")); setReason(""); }}>
                        Decide
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && !(data?.rows || []).length && (
                <tr><td colSpan="10" className="px-4 py-10 text-center text-slate-500 text-sm">No policies match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!decideOpen} onOpenChange={(o) => !o && setDecideOpen(null)}>
        <DialogContent data-testid="policy-decide-dialog">
          <DialogHeader>
            <DialogTitle>Decide policy · {decideOpen?.policy_number}</DialogTitle>
            <DialogDescription>
              {POLICY_TYPE_LABEL[decideOpen?.policy_type]} for <strong>{decideOpen?.policy_holder_name}</strong>.
              Requested: {fmtMoney(decideOpen?.requested_limit_usd || 0)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Decision</Label>
              <Select value={decision} onValueChange={setDecision}>
                <SelectTrigger data-testid="policy-decision"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="declined">Decline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {decision === "approved" && (
              <div className="space-y-1.5">
                <Label>Approved limit (USD)</Label>
                <Input type="number" value={limitOverride}
                  onChange={(e) => setLimitOverride(e.target.value)}
                  placeholder={String(decideOpen?.requested_limit_usd || "")}
                  data-testid="policy-limit"/>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Reason (audit, ≥3 chars)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder={decision === "approved" ? "Book passes underwriting · grade B" : "Insufficient business volume"}
                data-testid="policy-reason"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOpen(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitDecision} disabled={busy} data-testid="policy-decide-submit">
              <Send className="w-4 h-4 mr-1.5"/>{busy ? "Submitting…" : `Confirm ${decision}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// WAVE IV — Takeover queue aging (pre-day-60 buckets)
// ════════════════════════════════════════════════════════════════════════
export function InsuranceTakeoverAging() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState("<15d");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/partner/insurance/takeover-queue-aging`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load aging"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buckets = ["<15d", "15-29d", "30-44d", "45-54d", "55-60d"];
  const tone = { "<15d": "rose", "15-29d": "orange", "30-44d": "amber",
                 "45-54d": "sky", "55-60d": "slate" };
  const rows = (data?.buckets || {})[activeBucket] || [];
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ins-aging">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-600" /> Takeover Aging
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Pre-day-60 monitoring buckets · spot incoming load before it lands.
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="aging-refresh" disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {buckets.map(b => {
          const k = (data?.kpis || {})[b] || {};
          return (
            <button key={b} onClick={() => setActiveBucket(b)} data-testid={`aging-bucket-${b}`}
              className={`rounded-xl border px-4 py-3 text-left transition ${activeBucket === b ? `bg-${tone[b]}-50 border-${tone[b]}-300` : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{b}</div>
              <div className="text-2xl font-semibold tabular-nums mt-1">{k.count ?? 0}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{fmtMoneyP(k.total_owed_usd ?? 0)}</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Bucket: {activeBucket} · {rows.length} loans</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Loan #</th>
                <th className="px-4 py-2.5 font-medium">Funder</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Principal owed</th>
                <th className="px-4 py-2.5 font-medium">Total owed</th>
                <th className="px-4 py-2.5 font-medium">Due</th>
                <th className="px-4 py-2.5 font-medium">Days→takeover</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(l => (
                <tr key={l.id} data-testid={`aging-row-${l.id?.slice(0,6)}`}>
                  <td className="px-4 py-2 font-mono text-[10.5px]">{l.loan_number}</td>
                  <td className="px-4 py-2 text-[11px]"><Badge variant="outline">{l.funder_type}</Badge></td>
                  <td className="px-4 py-2"><StatusPill status={l.status}/></td>
                  <td className="px-4 py-2 tabular-nums">{fmtMoneyP(l.principal_remaining)}</td>
                  <td className="px-4 py-2 tabular-nums font-medium">{fmtMoneyP(l.total_owed)}</td>
                  <td className="px-4 py-2 text-[11px]">{fmtDate(l.due_date)}</td>
                  <td className="px-4 py-2 tabular-nums">{l.days_until_takeover}d</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500 text-sm">Bucket is empty.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// WAVE IV — Events feed
// ════════════════════════════════════════════════════════════════════════
export function InsuranceEvents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/partner/insurance/events?since_days=${sinceDays}&limit=300`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load events"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  const kindStyle = (k) => ({
    loan_opened:        "bg-sky-50 text-sky-700 border-sky-200",
    auto_debit_paid:    "bg-emerald-50 text-emerald-700 border-emerald-200",
    auto_debit_failed:  "bg-rose-50 text-rose-700 border-rose-200",
    takeover_filed:     "bg-violet-50 text-violet-700 border-violet-200",
    collection_payment: "bg-emerald-50 text-emerald-700 border-emerald-200",
    policy_decided:     "bg-amber-50 text-amber-700 border-amber-200",
  }[k] || "bg-slate-100 text-slate-700 border-slate-200");

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ins-events">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-sky-600" /> Activity Feed
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Time-ordered stream of every loan, takeover, debit, decision.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(parseInt(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="events-since"><SelectValue/></SelectTrigger>
            <SelectContent>
              {[1, 3, 7, 14, 30, 60, 90].map(d => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" disabled={loading}><RefreshCw className="w-4 h-4 mr-1.5"/>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {["loan_opened", "auto_debit_paid", "auto_debit_failed", "takeover_filed",
          "collection_payment", "policy_decided"].map(k => (
          <Kpi key={k} label={k.replace(/_/g, " ")} value={(data?.by_kind || {})[k] ?? 0}/>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Event</th>
              <th className="px-4 py-2.5 font-medium">Reference</th>
              <th className="px-4 py-2.5 font-medium">Amount</th>
              <th className="px-4 py-2.5 font-medium">Label</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.events || []).map((e, i) => (
              <tr key={i} data-testid={`event-row-${i}`}>
                <td className="px-4 py-2 text-[11px] text-slate-500">{fmtDT(e.at)}</td>
                <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${kindStyle(e.kind)}`}>{e.kind}</span></td>
                <td className="px-4 py-2 font-mono text-[10.5px]">{e.loan_number || e.policy_number || (e.holder_name || "").slice(0, 18) || "—"}</td>
                <td className="px-4 py-2 tabular-nums">{e.amount_usd != null ? fmtMoneyP(e.amount_usd) : "—"}</td>
                <td className="px-4 py-2 text-[12.5px]">{e.label}</td>
              </tr>
            ))}
            {!loading && !(data?.events || []).length && (
              <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500 text-sm">No events in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// WAVE V — Advanced Analytics
// ════════════════════════════════════════════════════════════════════════
export function InsuranceAdvancedAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(365);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/partner/insurance/analytics-advanced?since_days=${sinceDays}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  const lossTone = data?.loss_ratio_pct == null ? "slate"
    : data.loss_ratio_pct < 50 ? "emerald"
    : data.loss_ratio_pct < 100 ? "amber" : "rose";

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ins-advanced">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-600" /> Advanced Analytics
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Loss ratio · portfolio performance · concentration · monthly series.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(parseInt(v))}>
            <SelectTrigger className="w-32 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              {[90, 180, 365, 730, 1095].map(d => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-1.5"/>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Loss ratio"
              value={data?.loss_ratio_pct == null ? "—" : `${data.loss_ratio_pct}%`}
              icon={Activity} tone={lossTone} testid="adv-kpi-loss"/>
        <Kpi label="Claims paid" value={fmtMoney(data?.claims_paid_usd ?? 0)} icon={ArrowUpFromLine} tone="rose"/>
        <Kpi label="Premium received" value={fmtMoney(data?.premium_received_usd ?? 0)} icon={ArrowDownToLine} tone="emerald"/>
        <Kpi label="States covered" value={(data?.state_concentration || []).length} icon={ShieldCheck}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b"><h3 className="text-sm font-semibold text-slate-700">Seller portfolio · top 20 by defaults</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2 font-medium">Seller</th><th className="px-4 py-2 font-medium">Opened</th><th className="px-4 py-2 font-medium">Defaults</th><th className="px-4 py-2 font-medium">Default %</th><th className="px-4 py-2 font-medium">Principal</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.seller_portfolio || []).map((s, i) => (
                <tr key={s._id || i}>
                  <td className="px-4 py-2"><Stack top={s.seller_company || s.seller_email || (s._id || "").slice(0, 8)} sub={s.seller_email}/></td>
                  <td className="px-4 py-2 tabular-nums">{s.opened}</td>
                  <td className="px-4 py-2 tabular-nums">{s.defaulted}</td>
                  <td className={`px-4 py-2 tabular-nums ${s.default_rate_pct > 20 ? "text-rose-700 font-medium" : ""}`}>{s.default_rate_pct}%</td>
                  <td className="px-4 py-2 tabular-nums">{fmtMoneyP(s.principal_usd)}</td>
                </tr>
              ))}
              {!loading && !(data?.seller_portfolio || []).length && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500 text-[12.5px]">No seller portfolio data.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b"><h3 className="text-sm font-semibold text-slate-700">Lender portfolio · top 20 by defaults</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2 font-medium">Lender</th><th className="px-4 py-2 font-medium">Opened</th><th className="px-4 py-2 font-medium">Defaults</th><th className="px-4 py-2 font-medium">Default %</th><th className="px-4 py-2 font-medium">Principal</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.lender_portfolio || []).map((l, i) => (
                <tr key={l._id || i}>
                  <td className="px-4 py-2 font-medium">{l.lender_name || (l._id || "").slice(0, 8)}</td>
                  <td className="px-4 py-2 tabular-nums">{l.opened}</td>
                  <td className="px-4 py-2 tabular-nums">{l.defaulted}</td>
                  <td className={`px-4 py-2 tabular-nums ${l.default_rate_pct > 20 ? "text-rose-700 font-medium" : ""}`}>{l.default_rate_pct}%</td>
                  <td className="px-4 py-2 tabular-nums">{fmtMoneyP(l.principal_usd)}</td>
                </tr>
              ))}
              {!loading && !(data?.lender_portfolio || []).length && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500 text-[12.5px]">No lender portfolio data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b"><h3 className="text-sm font-semibold text-slate-700">State concentration heatmap</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2 font-medium">State</th><th className="px-4 py-2 font-medium">Opened</th><th className="px-4 py-2 font-medium">Defaults</th><th className="px-4 py-2 font-medium">Default %</th><th className="px-4 py-2 font-medium">Principal</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.state_concentration || []).slice(0, 20).map((s, i) => (
              <tr key={s._id || i}>
                <td className="px-4 py-2 font-mono">{s._id || "—"}</td>
                <td className="px-4 py-2 tabular-nums">{s.opened}</td>
                <td className="px-4 py-2 tabular-nums">{s.defaulted}</td>
                <td className={`px-4 py-2 tabular-nums ${s.default_rate_pct > 20 ? "text-rose-700 font-medium" : ""}`}>{s.default_rate_pct}%</td>
                <td className="px-4 py-2 tabular-nums">{fmtMoneyP(s.principal_usd)}</td>
              </tr>
            ))}
            {!loading && !(data?.state_concentration || []).length && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500 text-[12.5px]">No state concentration data.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b"><h3 className="text-sm font-semibold text-slate-700">Monthly series · premium vs claims</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2 font-medium">Month</th><th className="px-4 py-2 font-medium">Premium</th><th className="px-4 py-2 font-medium">Claims</th><th className="px-4 py-2 font-medium">Net</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.monthly_series || []).map((m, i) => {
              const net = (m.premium_usd || 0) - (m.claims_usd || 0);
              return (
                <tr key={i}>
                  <td className="px-4 py-2 font-mono text-[11px]">{m.month}</td>
                  <td className="px-4 py-2 tabular-nums text-emerald-700">{fmtMoneyP(m.premium_usd)}</td>
                  <td className="px-4 py-2 tabular-nums text-rose-700">{fmtMoneyP(m.claims_usd)}</td>
                  <td className={`px-4 py-2 tabular-nums font-medium ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoneyP(net)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Tiny utility shared across the new pages (locally defined to avoid touching imports)
function Stack({ top, sub }) {
  return (
    <div>
      <div className="text-[12.5px] font-medium">{top || "—"}</div>
      {sub && <div className="text-[10.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

